import { LIMITS } from '@tctp/protocol';
import { useEffect, useState } from 'react';

export const STAGE_W = 1600;
export const STAGE_H = 900;
export interface Point { x: number; y: number }
export const FELT_CENTRE: Point = { x: 800, y: 360 };

/**
 * Seat box centres by display position: nine seats on an ellipse around the felt — 0 = the viewer at the bottom centre, then
 * clockwise: the lower-left diagonal, the left middle, the upper-left diagonal, the two top seats (pushed out past the pot
 * pill's reach, 680..920), and down the right side. Nothing sits at the very top, where the pot pill is.
 */
export const SEAT_POSITIONS: readonly Point[] = [
  { x: 800, y: 690 },
  { x: 440, y: 642 }, { x: 250, y: 470 }, { x: 315, y: 275 }, { x: 544, y: 150 },
  { x: 1056, y: 150 }, { x: 1285, y: 275 }, { x: 1350, y: 470 }, { x: 1160, y: 642 },
];

/** The viewer's seat is drawn at position 0; a spectator sees seat n at position n. */
export function positionFor(seat: number, mySeat: number | null): number {
  return mySeat === null ? seat : (seat - mySeat + LIMITS.maxPlayers) % LIMITS.maxPlayers;
}

export function towardCentre(p: Point, fraction: number): Point {
  return { x: p.x + (FELT_CENTRE.x - p.x) * fraction, y: p.y + (FELT_CENTRE.y - p.y) * fraction };
}

export function chipPoint(position: number): Point {
  if (position === 0) return { x: 690, y: 482 };   // above the viewer's cards (673..927 from y 522), under the board (to 443): the chip beside the bubble, toward the pot
  if (position === 4) return { x: 500, y: 208 };   // the top row: under the pill's outer end, above the board (492..1108 × 285..443)
  if (position === 5) return { x: 1100, y: 208 };
  if (position === 3) return { x: 520, y: 254 };   // the upper diagonals: above the board's outer cards, since the line to the centre crosses them
  if (position === 6) return { x: 1080, y: 254 };
  return towardCentre(SEAT_POSITIONS[position]!, 0.38);
}

export const DEAL_STEP_MS = 75;   /* was 110: a nine-seat deal took 3.2 s before the last card landed, now 2.3 s */
/** Board cards within the flop land this far apart (PokerNow's ~0.35 s); hole cards keep DEAL_STEP_MS. */
export const FLOP_STEP_MS = 350;
export const DEAL_MS = 350;
/** A dealt card flips face-up over this long once it has landed (`.pcard.dealt.flip` in base.css). */
export const FLIP_MS = 400;
/** A card revealed in a player's hand flips over in place (`.pcard.reveal`); an all-in run-out deals the board after it. */
export const REVEAL_MS = 600;
/** Between the streets of an all-in run-out (the server's RUN_OUT_STREET_MS). */
export const STREET_GAP_MS = 4_000;

/**
 * Deal delays for board cards `prevLen..len-1` revealed in one snapshot: flop cards one FLOP_STEP apart, and a STREET_GAP
 * before the turn and before the river, so an all-in run-out (which arrives as one snapshot) is dealt one street at a time.
 */
export function boardDealDelays(prevLen: number, len: number): number[] {
  const street = (i: number): number => (i < 3 ? 0 : i - 2);
  const out: number[] = [];
  for (let i = prevLen; i < len; i++) out.push((street(i) - street(prevLen)) * STREET_GAP_MS + (i < 3 ? i - prevLen : 0) * FLOP_STEP_MS);
  return out;
}

/** Deal order: seats in the hand, clockwise from the seat after the button; seat → index, or null when not in the hand. */
export function dealOrder(inHand: boolean[], buttonSeat: number): Array<number | null> {
  const n = inHand.length;
  const out: Array<number | null> = Array(n).fill(null);
  let k = 0;
  for (let step = 1; step <= n; step++) {
    const s = (buttonSeat + step) % n;
    if (inHand[s]) out[s] = k++;
  }
  return out;
}

export interface Layout { portrait: boolean; width: number; height: number; scale: number }

/** Portrait viewports (taller than wide) get the 900 × 1600 stage of spec §12; everything else the 1600 × 900 stage of §2. */
export function layoutFor(vw: number, vh: number): Layout {
  const portrait = vh > vw;
  const [width, height] = portrait ? [STAGE_H, STAGE_W] : [STAGE_W, STAGE_H];
  return { portrait, width, height, scale: Math.min(vw / width, vh / height) };
}

/** Height of the bar the monitor draws above the stage (`.monitor-bar`); the stage scales into what is left. */
export const MONITOR_BAR_PX = 30;

export function useLayout(insetTop = 0): Layout {
  const [layout, setLayout] = useState(() => layoutFor(window.innerWidth, window.innerHeight - insetTop));
  useEffect(() => {
    const onResize = (): void => setLayout(layoutFor(window.innerWidth, window.innerHeight - insetTop));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [insetTop]);
  return layout;
}

export interface Geometry { centre: Point; seats: readonly Point[]; chips: readonly Point[]; bubbles: readonly Point[] }   // the dealer button sits in its player's pill, so it needs no point

const POSITIONS = Array.from({ length: LIMITS.maxPlayers }, (_, i) => i);
/**
 * Action bubble centres, each hugging its seat box like PokerNow's: the bottom row just above the box (right of the fanned
 * cards, left of the discard), the top row just below it (between its hanging cards and its discard, above its chip), the side
 * rows below (left) or above (right) the box, since the gap between the side boxes and the board holds the disc and the chip.
 */
export const BUBBLE_POSITIONS: readonly Point[] = [
  /* Every seat's cards stand over its own pill, so the bubble sits where no card can be: above the viewer's cards beside
     their chip, astride the pill's bottom edge for the side seats, past the pill's inner end and above the pot pill for the
     top row. */
  { x: 800, y: 482 },
  { x: 440, y: 694 }, { x: 250, y: 522 }, { x: 315, y: 327 }, { x: 736, y: 150 },
  { x: 864, y: 150 }, { x: 1285, y: 327 }, { x: 1350, y: 522 }, { x: 1160, y: 694 },
];
export const LANDSCAPE: Geometry = { centre: FELT_CENTRE, seats: SEAT_POSITIONS, chips: POSITIONS.map(chipPoint), bubbles: BUBBLE_POSITIONS };

/** Spec §12: seat anchors are bottom-centres; chips are a fixed table. Nine seats: the viewer at the bottom, four up each side, none at the top. */
const PORTRAIT_SEATS: readonly Point[] = [
  { x: 450, y: 1420 }, { x: 141, y: 1230 }, { x: 141, y: 996 }, { x: 141, y: 596 }, { x: 141, y: 358 },
  { x: 759, y: 358 }, { x: 759, y: 596 }, { x: 759, y: 996 }, { x: 759, y: 1230 },
];
const side = (pos: number, above: number, x = 302): Point => ({ x: pos < 5 ? x : 900 - x, y: PORTRAIT_SEATS[pos]!.y - above });
export const PORTRAIT: Geometry = {
  centre: { x: 450, y: 726 }, seats: PORTRAIT_SEATS,
  /** The viewer's chip sits just above their cards (which stand clear of the pill, top at 1145); the sides' between the box and the felt centre. */
  chips: POSITIONS.map(p => (p === 0 ? { x: 410, y: 1100 } : p === 1 || p === 8 ? side(p, 175, 330) : side(p, 113))),   // the lowest side seats' chips sit higher and further in, clear of the viewer's tilted discard
  /** 60 above the chip (the 44-tall pill clears the 72-tall chip), so each bubble hugs its own seat. */
  bubbles: POSITIONS.map(p => (p === 0 ? { x: 410, y: 1040 } : p === 1 || p === 8 ? side(p, 235) : side(p, 173))),
};

export function geometryFor(layout: Layout): Geometry {
  return layout.portrait ? PORTRAIT : LANDSCAPE;
}
